import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';
import isEqual from 'lodash.isequal';

import { Card } from '@/core/components/Card/Card';
import { ReportReadStatus, ReportSourceType } from '@/core/interfaces/common';
import { useAppDispatch } from '@/core/store/store';
import { useReportReadStatus } from '@/core/hooks/useReportReadStatus';

import { getActiveReportDataSelector, openDetailsModal } from '@/features/Reports/store';
import { getReportCategoriesNames, getReportCountriesNames } from '@/features/Reports/helpers';
import { Report } from '@/features/Reports/interfaces';

import { CardListProps } from './CardList.types';
import { CardContainer } from './CardList.styles';

export const CardList = ({ reports, onReportClick }: CardListProps) => {
  const dispatch = useAppDispatch();
  const { markReportInBrowser } = useReportReadStatus();

  const [reportsIds, setReportsIds] = useState<Array<number>>(
    reports.map(({ reportId }) => reportId)
  );
  const [cache] = useState(
    new CellMeasurerCache({
      fixedWidth: true,
      minHeight: 118,
    })
  );
  const { colors } = useTheme();
  const selectedReport = useSelector(getActiveReportDataSelector);

  const onAutoSizerResize = useCallback(() => {
    cache.clearAll();
  }, [cache]);

  const handleMarkAsClick = useCallback(
    (report: Report) => {
      const newStatus =
        report.readStatus === ReportReadStatus.READ
          ? ReportReadStatus.UNREAD
          : ReportReadStatus.READ;

      markReportInBrowser(report, newStatus);
    },
    [markReportInBrowser]
  );

  useEffect(() => {
    const newReportsIds = reports.map(({ reportId }) => reportId);

    /**
     * We have to clear cache only on reports list change,
     * otherwise reports list will scroll on any modification of report data
     * like click to open report details modal which chanage `wasOpened` field
     */
    if (!isEqual(reportsIds, newReportsIds)) {
      setReportsIds(newReportsIds);

      cache.clearAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  return (
    <AutoSizer onResize={onAutoSizerResize}>
      {({ height, width }) => (
        <List
          height={height}
          rowCount={reports.length}
          rowHeight={cache.rowHeight}
          width={width}
          containerStyle={{
            padding: 5,
          }}
          deferredMeasurementCache={cache}
          rowRenderer={({ index, key, parent, style }) => {
            const report = reports[index];

            const reportDate =
              report.reportSourceType === ReportSourceType.LOOKOUT
                ? report.modifiedDatetime
                : report.publishedDatetime;

            const handleReportClick = () => {
              dispatch(openDetailsModal(report));
              onReportClick?.(report);
            };

            return (
              <CellMeasurer
                cache={cache}
                columnIndex={0}
                key={key}
                parent={parent}
                rowIndex={index}
              >
                {({ measure }) => (
                  // 'style' attribute required to position cell (within parent List)
                  <CardContainer
                    style={style}
                    onLoad={measure}
                  >
                    <Card
                      key={report.reportId}
                      onClick={handleReportClick}
                      title={report.reportTitle}
                      country={getReportCountriesNames(report.country)}
                      categories={getReportCategoriesNames(report.category)}
                      source={report.reportSourceType}
                      type={report.reportType}
                      impact={report.impact}
                      date={reportDate}
                      borderColor={
                        selectedReport?.reportId === report.reportId
                          ? colors.impact[selectedReport.impact].backgroundColor
                          : undefined
                      }
                      wasOpened={report.wasOpened}
                      readStatus={report.readStatus}
                      onMarkAsClick={() => handleMarkAsClick(report)}
                    />
                  </CardContainer>
                )}
              </CellMeasurer>
            );
          }}
        />
      )}
    </AutoSizer>
  );
};
